const KST_OFFSET = '+09:00'
const DEFAULT_ORDERING = '-created_at'
const DEFAULT_PAGE_SIZE = 50
const ACTOR_TYPES = new Set(['user', 'system', 'external', 'unknown'])
const ORDERINGS = new Set([DEFAULT_ORDERING, 'created_at'])
export const WORK_EVENT_ROUTE_QUERY_KEYS = Object.freeze([
  'log_from',
  'log_to',
  'log_event_type',
  'log_actor_type',
  'log_actor_user_id',
  'log_work_id',
  'log_ordering',
  'log_page',
])

function firstQueryValue(value) {
  return Array.isArray(value) ? value[0] : value
}

function positiveInteger(value) {
  const parsed = Number(firstQueryValue(value))
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

function validDateText(value) {
  const text = String(firstQueryValue(value) || '')
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text)
  if (!match) return ''
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(Date.UTC(year, month - 1, day))
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) return ''
  return text
}

function nextDateText(value) {
  const [year, month, day] = value.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day + 1))
  return [
    String(date.getUTCFullYear()).padStart(4, '0'),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0'),
  ].join('-')
}

function filterOptionList(rows) {
  if (!Array.isArray(rows)) return []
  const seen = new Set()
  return rows.reduce((result, row) => {
    const value = row?.value
    if (value === undefined || value === null || value === '') return result
    const key = String(value)
    if (seen.has(key)) return result
    seen.add(key)
    result.push({ value, text: String(row?.label || value) })
    return result
  }, [])
}

export function formatWorkEventDateTime(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date).reduce((result, part) => ({
    ...result,
    [part.type]: part.value,
  }), {})
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`
}

export function eventTypeText(event = {}) {
  return String(event.event_type_label || '').trim() || '기타 이벤트'
}

export function eventSummaryText(event = {}) {
  if (event.details_availability === 'restricted') {
    return '접근 권한이 없어 상세 내용을 표시할 수 없습니다.'
  }
  return String(event.display_message || '').trim() || '업무 이벤트가 기록되었습니다.'
}

export function eventNoteText(event = {}) {
  if (event.details_availability === 'restricted') return ''
  return String(event.display_note || '').trim()
}

export function eventActorText(event = {}) {
  const actor = event.actor || {}
  if (actor.type === 'system') return '시스템'
  if (actor.type === 'external') {
    const name = String(actor.name || '').trim()
    return name ? `외부 연동 · ${name}` : '외부 연동'
  }
  if (actor.type !== 'user') return '실행자 정보 없음'
  const name = String(actor.name || '').trim()
  const username = String(actor.username || '').trim()
  if (name && username && name !== username) return `${name} (${username})`
  if (name || username) return name || username
  return actor.user_id ? `사용자 #${actor.user_id}` : '실행자 정보 없음'
}

export function eventWorkText(event = {}) {
  const name = String(event.work_name || '').trim()
  if (name) return name
  return event.work ? `업무 #${event.work}` : '-'
}

export function eventTargetText(event = {}) {
  const name = String(event.work_item_name || '').trim()
  if (name) return name
  return event.work_item ? `할일 #${event.work_item}` : '업무 전체'
}

export function normalizeWorkEventFilterOptions(options = {}) {
  return {
    works: filterOptionList(options.works),
    eventTypes: filterOptionList(options.event_types),
    actors: filterOptionList(options.actors),
  }
}

export function normalizeWorkEventListPayload(payload = {}) {
  const rows = Array.isArray(payload.data) ? payload.data : []
  return {
    data: rows.map(event => ({
      ...event,
      work_name: event?.work_name || '',
      work_item_name: event?.work_item_name || '',
      actor: event?.actor && typeof event.actor === 'object'
        ? {
          type: event.actor.type || 'unknown',
          user_id: event.actor.user_id ?? null,
          name: event.actor.name || '',
          username: event.actor.username || '',
        }
        : { type: 'unknown', user_id: null, name: '', username: '' },
    })),
    filterOptions: normalizeWorkEventFilterOptions(payload.filter_options),
  }
}

export function parseWorkEventRouteQuery(query = {}) {
  const actorTypeValue = String(firstQueryValue(query.log_actor_type) || '')
  const actorType = ACTOR_TYPES.has(actorTypeValue) ? actorTypeValue : ''
  const orderingValue = String(firstQueryValue(query.log_ordering) || '')
  return {
    from: validDateText(query.log_from),
    to: validDateText(query.log_to),
    eventType: String(firstQueryValue(query.log_event_type) || ''),
    actorType,
    actorUserId: actorType === 'user' ? positiveInteger(query.log_actor_user_id) : null,
    workId: positiveInteger(query.log_work_id),
    ordering: ORDERINGS.has(orderingValue) ? orderingValue : DEFAULT_ORDERING,
    page: positiveInteger(query.log_page) || 1,
    limit: DEFAULT_PAGE_SIZE,
  }
}

export function workEventRouteQuery(filters = {}) {
  const query = {}
  if (validDateText(filters.from)) query.log_from = filters.from
  if (validDateText(filters.to)) query.log_to = filters.to
  if (filters.eventType) query.log_event_type = String(filters.eventType)
  if (ACTOR_TYPES.has(filters.actorType)) query.log_actor_type = filters.actorType
  if (filters.actorType === 'user' && positiveInteger(filters.actorUserId)) {
    query.log_actor_user_id = String(filters.actorUserId)
  }
  if (positiveInteger(filters.workId)) query.log_work_id = String(filters.workId)
  if (filters.ordering === 'created_at') query.log_ordering = 'created_at'
  if ((positiveInteger(filters.page) || 1) > 1) query.log_page = String(filters.page)
  return query
}

export function buildWorkEventRequestQuery(filters = {}) {
  const from = validDateText(filters.from)
  const to = validDateText(filters.to)
  if (from && to && from > to) {
    throw new Error('시작일은 종료일보다 늦을 수 없습니다.')
  }
  const query = {
    ordering: ORDERINGS.has(filters.ordering) ? filters.ordering : DEFAULT_ORDERING,
    page: positiveInteger(filters.page) || 1,
    limit: positiveInteger(filters.limit) || DEFAULT_PAGE_SIZE,
  }
  if (from) query.created_from = `${from}T00:00:00${KST_OFFSET}`
  if (to) query.created_to = `${nextDateText(to)}T00:00:00${KST_OFFSET}`
  if (filters.eventType) query.event_type = String(filters.eventType)
  if (ACTOR_TYPES.has(filters.actorType)) query.actor_type = filters.actorType
  if (filters.actorType === 'user' && positiveInteger(filters.actorUserId)) {
    query.actor_user_id = positiveInteger(filters.actorUserId)
  }
  if (positiveInteger(filters.workId)) query.work_id = positiveInteger(filters.workId)
  return query
}
